import { useContext, useId, useMemo, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { klickZiel, gruppiert, MitteilungOrtKontext, mitteilungZeit, useEineGelesen, type Mitteilung, type OrtQuelle } from "../mitteilungen";
import { personNach, usePersonen, type PersonKurz } from "../personen";
import { zeitpunkt } from "../zeit";
import { Avatar } from "../ui/Avatar";
import { Icon } from "./Icon";
import s from "./mitteilungen.module.css";

/** Liste der Mitteilungen für die Leiste rechts und die Seite /mitteilungen (KOPFZEILE.md, Abschnitt 3.4): Gruppen „Neu“ und
 *  „Früher“, je Eintrag Bild der auslösenden Person, Text, Zeit und Ort. Ein Klick setzt genau diese Mitteilung gelesen, auch ohne Link. */
export function MitteilungenListe({ liste, ueber, leer }: { liste: readonly Mitteilung[]; ueber: "seite" | "leiste"; leer: ReactNode }) {
  const quellen = useContext(MitteilungOrtKontext);
  const links = useMemo(() => [...new Set(liste.flatMap((m) => (m.link ? [m.link] : [])))], [liste]);
  if (liste.length === 0) return <>{leer}</>;
  return <MitOrten quellen={quellen} links={links} bisher={new Map()} liste={liste} ueber={ueber} />;
}

interface OrteProps {
  quellen: readonly OrtQuelle[];
  links: readonly string[];
  bisher: ReadonlyMap<string, string>;
  liste: readonly Mitteilung[];
  ueber: "seite" | "leiste";
}

/** Ruft die Ort-Hooks der Module nacheinander auf, je einer in einer eigenen Komponente (wie die Zähler in der Shell). */
function MitOrten({ quellen, links, bisher, liste, ueber }: OrteProps) {
  const [erste, ...rest] = quellen;
  if (!erste) return <Gruppen liste={liste} ueber={ueber} orte={bisher} />;
  return <OrtAus quelle={erste} quellen={rest} links={links} bisher={bisher} liste={liste} ueber={ueber} />;
}

function OrtAus({ quelle, ...rest }: OrteProps & { quelle: OrtQuelle }) {
  const orte = quelle(rest.links);
  return <MitOrten {...rest} bisher={new Map([...orte, ...rest.bisher])} />;
}

function Gruppen({ liste, ueber, orte }: { liste: readonly Mitteilung[]; ueber: "seite" | "leiste"; orte: ReadonlyMap<string, string> }) {
  const personen = usePersonen();
  const gelesen = useEineGelesen();
  const { neu, frueher } = gruppiert(liste);
  const eintrag = (m: Mitteilung) => (
    <Eintrag
      key={m.id}
      m={m}
      ueber={ueber}
      ort={m.link ? orte.get(m.link) : undefined}
      person={personNach(personen.data, m.ausloeser)}
      onKlick={() => {
        if (!m.gelesen_am) gelesen.mutate(m.id);
      }}
    />
  );
  return (
    <div className={s.liste}>
      {neu.length > 0 ? <Gruppe titel="Neu">{neu.map(eintrag)}</Gruppe> : null}
      {frueher.length > 0 ? <Gruppe titel="Früher">{frueher.map(eintrag)}</Gruppe> : null}
    </div>
  );
}

function Gruppe({ titel, children }: { titel: string; children: ReactNode }) {
  const id = useId();
  return (
    <>
      <p className={s.gruppe} id={id}>
        {titel}
      </p>
      <ul className={s.eintraege} aria-labelledby={id}>
        {children}
      </ul>
    </>
  );
}

function Eintrag({ m, ueber, ort, person, onKlick }: { m: Mitteilung; ueber: "seite" | "leiste"; ort: string | undefined; person: PersonKurz | undefined; onKlick: () => void }) {
  const lage = useLocation();
  const ziel = klickZiel(m.link, lage.search, ueber, lage.state);
  const neu = !m.gelesen_am;
  const inhalt = (
    <>
      {person ? (
        <Avatar person={person} groesse={36} />
      ) : (
        <span className={s.symbol} aria-hidden="true">
          <Icon name="uhr" />
        </span>
      )}
      <span className={s.text}>
        <span className={s.titel}>
          {neu ? <span className="nur-leser">Ungelesen: </span> : null}
          {m.titel}
        </span>
        {m.text ? <span className={s.zusatz}>{m.text}</span> : null}
        <span className={s.meta}>
          <time dateTime={m.erstellt_am} title={zeitpunkt(m.erstellt_am)}>
            {mitteilungZeit(m.erstellt_am)}
          </time>
          {ort ? <span>{ort}</span> : null}
        </span>
      </span>
      <span className={s.punkt} aria-hidden="true" />
    </>
  );
  return (
    <li>
      {ziel ? (
        <Link to={ziel.to} replace={ziel.replace} state={ziel.state} className={s.eintrag} data-neu={neu} onClick={onKlick}>
          {inhalt}
        </Link>
      ) : (
        <button type="button" className={s.eintrag} data-neu={neu} onClick={onKlick}>
          {inhalt}
        </button>
      )}
    </li>
  );
}
